import type { ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import Avatar from '../Custom/Avatar';
import Box from '../UI/Box';

export const EventListItemTeamItemFragment = graphql(`
  fragment EventListItemTeamItem on event_team {
    event_id
    team_id
    score
    team {
      id
      avatar_id
      title
      home_primary_color
    }
  }
`);

export const EventListItemTeamRsvpFragment = graphql(`
  fragment EventListItemTeamRsvp on event_team {
    event_id
    team_id
    rsvp_summary {
      event_id
      team_id
      yes
      maybe
      no
    }
  }
`);

type EventListItemTeamItemProps = {
  fragment: FragmentOf<typeof EventListItemTeamItemFragment> | null | undefined;
  rsvpFragment?:
    | FragmentOf<typeof EventListItemTeamRsvpFragment>
    | null
    | undefined;
  isPastGame: boolean;
  reverse?: boolean;
  showLabel?: boolean;
  style?: ViewStyle;
};

function TeamRsvpCountItem({
  count,
  icon,
  testID,
}: {
  count: number;
  icon: 'checkmark' | 'help' | 'close';
  testID: string;
}) {
  return (
    <Box alignItems="center" direction="horizontal" space="xs" testID={testID}>
      <Text size="sm" variant="typographySecondary">
        {count}
      </Text>
      <Icon name={icon} size="sm" variant="typographySecondary" />
    </Box>
  );
}

function TeamRsvpCountStrip({
  counts,
  teamId,
}: {
  counts: { yes: number; maybe: number; no: number };
  teamId: string;
}) {
  return (
    <Box
      alignItems="center"
      direction="horizontal"
      space="md"
      testID={`EventTeamRsvpCount-${teamId}`}
    >
      <TeamRsvpCountItem
        count={counts.yes}
        icon="checkmark"
        testID={`TeamRsvpCountYes-${teamId}`}
      />
      <TeamRsvpCountItem
        count={counts.maybe}
        icon="help"
        testID={`TeamRsvpCountMaybe-${teamId}`}
      />
      <TeamRsvpCountItem
        count={counts.no}
        icon="close"
        testID={`TeamRsvpCountNo-${teamId}`}
      />
    </Box>
  );
}

export default function EventListItemTeamItem({
  fragment,
  rsvpFragment,
  isPastGame,
  showLabel = true,
  style: extraStyles,
}: EventListItemTeamItemProps) {
  const eventTeam = readFragment(EventListItemTeamItemFragment, fragment);
  const rsvpEventTeam = readFragment(
    EventListItemTeamRsvpFragment,
    rsvpFragment,
  );
  const counts = rsvpEventTeam?.rsvp_summary;
  const rsvpCounts = {
    yes: counts?.yes ?? 0,
    maybe: counts?.maybe ?? 0,
    no: counts?.no ?? 0,
  };
  const hasRsvpCounts = rsvpCounts.yes + rsvpCounts.maybe + rsvpCounts.no > 0;

  return (
    <Box px="lg" style={[style.container, extraStyles]} space="lg">
      <Avatar
        size="lg"
        id={eventTeam?.team.avatar_id || ''}
        name={eventTeam?.team.title || ''}
        color={eventTeam?.team.home_primary_color}
        testID={`EventListTeamAvatar-${eventTeam?.team_id ?? ''}`}
      />
      {showLabel ? (
        <Box flex space="xs">
          <Text
            numberOfLines={2}
            style={style.title}
            testID={`EventListTeamTitle-${eventTeam?.team_id ?? ''}`}
          >
            {eventTeam?.team.title}
          </Text>
          {counts && hasRsvpCounts ? (
            <TeamRsvpCountStrip
              counts={rsvpCounts}
              teamId={eventTeam?.team_id ?? ''}
            />
          ) : null}
        </Box>
      ) : null}
      {showLabel && isPastGame ? (
        <Text numberOfLines={1} style={style.score}>
          {eventTeam?.score}
        </Text>
      ) : null}
    </Box>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  title: {
    flexShrink: 1,
    flexWrap: 'wrap',
    width: '100%',
    fontSize: {
      base: theme.fontSize.xl,
      lg: theme.fontSize['3xl'],
    },
  },
  score: {
    textAlign: 'center',
    width: {
      base: theme.size(12),
      lg: theme.size(20),
    },
    fontSize: {
      base: theme.fontSize['2xl'],
      lg: theme.fontSize['4xl'],
    },
  },
}));
